import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Collapse, Input, List, InputNumber, Modal, Select, Space, Spin, Switch, Tag, Typography, message } from 'antd';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import API_BASE_URL from '../../helpers/api/apiConfig';
import { BoundaryImpact, boundaryImpactFeatures, geometryPositions, impactFeatureId } from './boundaryImpactMap';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

type Props = {
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, error: any) => Promise<boolean>;
};
type Impact = BoundaryImpact;
const token = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';
const impactCount = (impact: Impact) => impact.outsideAreas.length + impact.unlocatedAreas.length + impact.outsidePatternPoints.length;

const BackOfficeBoundariesTab: React.FC<Props> = ({ buildAuthHeaders, onAuthMismatch }) => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState<number>();
  const [policy, setPolicy] = useState<any>(null);
  const [enabled, setEnabled] = useState(false);
  const [geometry, setGeometry] = useState<any>(null);
  const [json, setJson] = useState('');
  const [geometryError, setGeometryError] = useState('');
  const [age, setAge] = useState(60);
  const [accuracy, setAccuracy] = useState(50);
  const [reason, setReason] = useState('');
  const [impact, setImpact] = useState<Impact>();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map>();
  const draw = useRef<MapboxDraw>();
  const fitNext = useRef(true);
  const popup = useRef<mapboxgl.Popup>();
  const impactFeatures = useMemo(() => boundaryImpactFeatures(impact), [impact]);
  const impactRows = useMemo(() => impact ? [
    ...impact.outsideAreas.map(row => ({ ...row, kind: 'area' as const, status: 'Outside boundary', color: 'red' })),
    ...impact.unlocatedAreas.map(row => ({ ...row, kind: 'area' as const, status: 'Missing location', color: 'orange' })),
    ...impact.outsidePatternPoints.map(row => ({ ...row, name: row.label || `Point #${row.id}`, kind: 'point' as const, status: 'Outside pattern point', color: 'red' })),
  ] : [], [impact]);
  const fitPositions = useCallback((positions: [number, number][]) => {
    if (!map.current || !positions.length) return;
    const bounds = positions.reduce((result, position) => result.extend(position), new mapboxgl.LngLatBounds());
    map.current.fitBounds(bounds, { padding: 65, maxZoom: 17 });
  }, []);
  const showImpactFeature = useCallback((feature: any, at?: [number, number]) => {
    const instance = map.current;
    if (!instance) return;
    const positions = geometryPositions(feature.geometry);
    if (!positions.length) return;
    if (!at) fitPositions(positions);
    const content = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = feature.properties.label;
    const detail = document.createElement('div');
    detail.textContent = `${feature.properties.status}${feature.properties.archived ? ' · Archived' : ''}${feature.properties.patternName ? ` · ${feature.properties.patternName}` : ''}${String(feature.properties.locationSource).startsWith('slam_') ? ' · Lock in space' : ''}`;
    content.append(title, detail);
    popup.current?.remove();
    popup.current = new mapboxgl.Popup({ offset: 10 }).setLngLat(at || positions[0]).setDOMContent(content).addTo(instance);
  }, [fitPositions]);
  const selection = useRef(companyId);
  selection.current = companyId;

  const request = useCallback(async (path: string, method = 'GET', body?: any) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method, headers: buildAuthHeaders(), credentials: 'include',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok) {
      if (await onAuthMismatch(response, result)) throw new Error('Please log in again');
      throw new Error(result.message || 'Boundary request failed');
    }
    return result;
  }, [buildAuthHeaders, onAuthMismatch]);

  useEffect(() => {
    let active = true;
    request('/companies?scope=all').then(result => { if (active) setCompanies(Array.isArray(result) ? result : result.data || []); })
      .catch(error => message.error(error.message));
    return () => { active = false; };
  }, [request]);

  useEffect(() => {
    if (!companyId) return;
    let active = true;
    setLoading(true); setPolicy(null); setImpact(undefined); setReason(''); setGeometry(null); setJson(''); setGeometryError('');
    fitNext.current = true;
    request(`/companies/${companyId}/operating-boundary`).then(({ data }) => {
      if (!active) return;
      setPolicy(data); setEnabled(data.enabled); setGeometry(data.geometry);
      setJson(data.geometry ? JSON.stringify(data.geometry, null, 2) : '');
      setAge(data.maxLocationAgeSeconds); setAccuracy(data.maxAccuracyMeters);
    }).catch(error => { if (active) message.error(error.message); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [companyId, request]);

  useEffect(() => {
    if (!container.current) return;
    const instance = new mapboxgl.Map({ container: container.current, accessToken: token,
      style: 'mapbox://styles/mapbox/satellite-streets-v12', center: [-0.14, 51.5], zoom: 12 });
    const control = new MapboxDraw({ displayControlsDefault: false, controls: { polygon: true, trash: true } });
    instance.on('load', () => setMapReady(true));
    instance.addControl(control as any, 'top-left');
    instance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    const update = () => {
      const polygons = control.getAll().features.flatMap((feature: any) => feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : []);
      const next = polygons.length ? { type: 'MultiPolygon', coordinates: polygons } : null;
      setGeometry(next); setJson(next ? JSON.stringify(next, null, 2) : ''); setGeometryError(''); setImpact(undefined);
    };
    const clickImpact = (event: any) => {
      if ((control as any).getMode() !== 'simple_select') return;
      const feature = event.features?.[0];
      if (feature) showImpactFeature(feature, [event.lngLat.lng, event.lngLat.lat]);
    };
    for (const layer of ['boundary-impact-fill', 'boundary-impact-line', 'boundary-impact-points']) {
      instance.on('click', layer, clickImpact);
      instance.on('mouseenter', layer, () => { instance.getCanvas().style.cursor = 'pointer'; });
      instance.on('mouseleave', layer, () => { instance.getCanvas().style.cursor = ''; });
    }
    instance.on('draw.create', update); instance.on('draw.update', update); instance.on('draw.delete', update);
    map.current = instance; draw.current = control;
    return () => { popup.current?.remove(); instance.remove(); map.current = undefined; draw.current = undefined; };
  }, [showImpactFeature]);

  useEffect(() => {
    const instance = map.current;
    const control = draw.current;
    if (!instance || !control || !mapReady) return;
    const apply = () => {
      const polygons = !geometry ? [] : geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
      (control as any).set({ type: 'FeatureCollection', features: polygons.map((coordinates: any) => ({
        type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates },
      })) });
      if (fitNext.current && polygons.length) {
        const positions = polygons.flat(2);
        const bounds = positions.reduce((result: mapboxgl.LngLatBounds, p: any) => result.extend(p), new mapboxgl.LngLatBounds());
        instance.fitBounds(bounds, { padding: 50, maxZoom: 17 }); fitNext.current = false;
      }
    };
    apply();
  }, [geometry, mapReady]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !mapReady) return;
    popup.current?.remove();
    const apply = () => {
      const source = instance.getSource('boundary-impact') as mapboxgl.GeoJSONSource | undefined;
      if (source) source.setData(impactFeatures);
      else {
        instance.addSource('boundary-impact', { type: 'geojson', data: impactFeatures });
        const color: any = ['match', ['get', 'status'], 'Unlocated area', '#f59e0b', '#ef4444'];
        instance.addLayer({ id: 'boundary-impact-fill', type: 'fill', source: 'boundary-impact',
          filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': color, 'fill-opacity': 0.28 } });
        instance.addLayer({ id: 'boundary-impact-line', type: 'line', source: 'boundary-impact',
          filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'line-color': color, 'line-width': 3 } });
        instance.addLayer({ id: 'boundary-impact-points', type: 'circle', source: 'boundary-impact',
          filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-color': color, 'circle-radius': 7, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 } });
      }
      if (impactFeatures.features.length) fitPositions([
        ...geometryPositions(geometry), ...impactFeatures.features.flatMap(feature => geometryPositions(feature.geometry)),
      ]);
    };
    apply();
  }, [impactFeatures, geometry, fitPositions, mapReady]);

  const editJson = (text: string) => {
    setJson(text); setImpact(undefined);
    if (!text.trim()) { setGeometry(null); setGeometryError(''); return; }
    try {
      const value = JSON.parse(text);
      if (!['Polygon', 'MultiPolygon'].includes(value.type) || !Array.isArray(value.coordinates)) throw new Error();
      // Full coordinate/topology validation is performed by the backend preview and save.
      setGeometryError('Use Preview to validate coordinates and polygon topology.');
      // Avoid passing malformed coordinate trees into the map library.
      const polygons = value.type === 'Polygon' ? [value.coordinates] : value.coordinates;
      if (text.length > 2000000 || !polygons.length || polygons.some((polygon: any) => !Array.isArray(polygon) || !polygon.length || polygon.some((ring: any) => !Array.isArray(ring) || ring.length < 4 || JSON.stringify(ring[0]) !== JSON.stringify(ring[ring.length - 1]) || ring.some((point: any) => !Array.isArray(point) || point.length < 2 || !point.every((n: any) => typeof n === 'number' && Number.isFinite(n)) || Math.abs(point[0]) > 180 || Math.abs(point[1]) > 90)))) throw new Error();
      setGeometry(value); setGeometryError(''); fitNext.current = true;
    } catch { setGeometryError('Enter a valid GeoJSON Polygon or MultiPolygon with longitude/latitude coordinates.'); }
  };
  const body = { enabled, geometry, maxLocationAgeSeconds: age, maxAccuracyMeters: accuracy };
  const preview = async () => {
    const targetCompanyId = companyId;
    setBusy(true);
    try { const result = await request(`/companies/${targetCompanyId}/operating-boundary/preview`, 'POST', body); if (selection.current !== targetCompanyId) return undefined; setImpact(result.data); return result.data as Impact; }
    catch (error: any) { message.error(error.message); return undefined; }
    finally { setBusy(false); }
  };
  const save = async () => {
    const targetCompanyId = companyId;
    const targetVersion = policy.version;
    const targetBody = { ...body, reason: reason.trim(), expectedVersion: targetVersion };
    const affected = await preview();
    if (!affected || selection.current !== targetCompanyId) return;
    Modal.confirm({ title: `${enabled ? 'Enforce boundary' : 'Save disabled boundary'} for ${companies.find(company => company.id === targetCompanyId)?.name || `company #${targetCompanyId}`}?`,
      content: `${impactCount(affected)} affected resource(s). Resources outside the boundary or missing outdoor geometry / an indoor lock in space will be blocked for operations. Historical reports remain available. Device capture enforcement requires the Lightbox update.`,
      okText: 'Save boundary',
      onOk: async () => {
        setBusy(true);
        try {
          const { data } = await request(`/companies/${targetCompanyId}/operating-boundary`, 'PUT', { ...targetBody, acknowledgeImpacts: true });
          if (selection.current === targetCompanyId) { setPolicy(data); setReason(''); }
          message.success('Company boundary saved');
        } catch (error: any) { message.error(error.message); throw error; }
        finally { setBusy(false); }
      },
    });
  };
  const disabled = !policy || loading || busy;
  return <Space direction="vertical" size="middle" style={{ width: '100%' }}>
    <Alert type="info" showIcon message="Company operating boundary" description="Only Back Office master users can define this boundary. Draw one or more polygons, or paste GeoJSON to include holes. Server checks apply when enabled. Lightbox still needs device-side capture and offline enforcement." />
    <Select aria-label="Company" placeholder="Select a company" showSearch optionFilterProp="label" value={companyId} disabled={busy}
      style={{ minWidth: 300 }} onChange={setCompanyId} options={companies.map(company => ({ value: company.id, label: `${company.name} (#${company.id})` }))} />
    <Spin spinning={loading}>
      <Card title="Allowed operating geography">
        <div ref={container} style={{ height: 430, pointerEvents: disabled ? 'none' : 'auto', opacity: disabled ? 0.5 : 1 }} />
        <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
          {impact && <Space wrap>
            <Tag color="red">Outside boundary</Tag><Tag color="orange">Missing location</Tag>
            <Button size="small" disabled={!impactFeatures.features.length} onClick={() => fitPositions(impactFeatures.features.flatMap(feature => geometryPositions(feature.geometry)))}>Show all affected locations</Button>
            <Button size="small" disabled={!geometry} onClick={() => fitPositions(geometryPositions(geometry))}>Show boundary</Button>
            <Typography.Text type="secondary">Click an affected shape or choose Show on map below.</Typography.Text>
          </Space>}
          <Typography.Text type="secondary">Approved area selection permits use without GPS; its centre can locate measurements. GPS age and accuracy limits apply when using no predefined area.</Typography.Text>
          <Typography.Text type="secondary">Outdoor areas use their full footprint. Indoor and mixed areas use their saved lock in space. Boundary edges are included; holes and gaps are excluded.</Typography.Text>
          <Input.TextArea aria-label="Boundary GeoJSON" rows={6} value={json} onChange={event => editJson(event.target.value)} disabled={disabled} placeholder="GeoJSON Polygon or MultiPolygon" />
          {geometryError && <Alert type="error" message={geometryError} />}
          <Space wrap>
            <Switch checked={enabled} disabled={disabled} onChange={value => { setEnabled(value); setImpact(undefined); }} checkedChildren="Enabled" unCheckedChildren="Disabled" />
            <Typography.Text>Maximum GPS age (seconds)</Typography.Text><InputNumber aria-label="Maximum GPS age" min={5} max={3600} value={age} disabled={disabled} onChange={value => { setAge(value || 60); setImpact(undefined); }} />
            <Typography.Text>Maximum GPS accuracy (metres)</Typography.Text><InputNumber aria-label="Maximum GPS accuracy" min={0.1} max={1000} value={accuracy} disabled={disabled} onChange={value => { setAccuracy(value || 50); setImpact(undefined); }} />
          </Space>
          {policy && <Typography.Text>Revision {policy.version} · Server mode: {policy.enforcementMode} · Saved state: {policy.state}</Typography.Text>}
          <Input.TextArea aria-label="Reason for boundary change" maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} disabled={disabled} placeholder="Reason for this change (required for the audit history)" />
          <Space>
            <Button onClick={preview} disabled={disabled || !!geometryError || (enabled && !geometry)}>Preview affected resources</Button>
            <Button type="primary" onClick={save} disabled={disabled || !!geometryError || (enabled && !geometry) || !reason.trim()}>Review and save</Button>
          </Space>
          {impact && <Alert type={impactCount(impact) ? 'warning' : 'success'} showIcon message={`${impact.outsideAreas.length} outside areas · ${impact.unlocatedAreas.length} areas missing geometry or lock in space · ${impact.outsidePatternPoints.length} outside GPS pattern points`}
            description={<>
              <Typography.Paragraph>Preview covers saved areas and GPS pattern points, including archived records. Existing measurements and reports are preserved.</Typography.Paragraph>
              <List size="small" dataSource={impactRows} locale={{ emptyText: 'No affected resources' }} renderItem={row => {
                const feature = impactFeatures.features.find(item => item.properties?.featureId === impactFeatureId(row.kind, row.id));
                return <List.Item actions={[feature
                  ? <Button key="map" size="small" onClick={() => showImpactFeature(feature)}>Show on map</Button>
                  : <Typography.Text key="unlocated" type="secondary">No usable geographic coordinates</Typography.Text>]}>
                  <Space wrap><Tag color={row.color}>{row.status}</Tag><Typography.Text>{row.name} (#{row.id}){row.archivedAt ? ' · Archived' : ''}</Typography.Text>{String(row.locationSource).startsWith('slam_') && <Tag color="blue">Lock in space</Tag>}{row.reason && <Typography.Text type="secondary">{row.reason}</Typography.Text>}</Space>
                </List.Item>;
              }} />
              <Collapse ghost items={[{ key: 'raw', label: 'Raw preview data', children: <pre style={{ maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{JSON.stringify(impact, null, 2)}</pre> }]} />
            </>} />}
        </Space>
      </Card>
    </Spin>
  </Space>;
};
export default BackOfficeBoundariesTab;
